// @flow
import * as React from 'react';

import AdminNewAutoMessageCompose from './AdminNewAutoMessageCompose.jsx';
import AdminNewAutoMessageCreate from './AdminNewAutoMessageCreate.jsx';
import AdminNewAutoMessageFormatCSV from './AdminNewAutoMessageFormatCSV.jsx';
import AdminNewAutoMessageForms from './AdminNewAutoMessageForms.jsx';
import AdminNewAutoMessageReview from './AdminNewAutoMessageReview.jsx';
import AdminNewAutoMessageSyncData from './AdminNewAutoMessageSyncData.jsx';
import Button from 'components/Button.jsx';
import LoadingModal from 'components/LoadingModal.jsx';
import ToastActions from 'actions/ToastActions.js';
import WizardLayout from 'components/WizardLayout';

import type { AutoMessage, AutoMessageOrg } from './';

type Props = {
  onBack: () => void,
  onFieldChange: () => void,
  onResetFieldChange: () => void,
  orgs: AutoMessageOrg[],
  loading: boolean,
  save: (input: SaveAutoMessageInput) => Promise<*>,
  autoMessage: *,
  startingTab: ?*,
};

type State = {
  autoMessageFields: AutoMessage,
};

class AdminNewAutoMessage extends React.Component<Props, State> {
  state = {
    autoMessageFields: {
      sftpCredentials: undefined,
      messageName: undefined,
      orgUuid: undefined,
      csvName: undefined,
      uuid: '',
      status: 'draft',
      includeParents: false,
      includeStudents: false,
      messageContent: undefined,
      sender: undefined,
      isActivatable: false,
      permissions: {
        canEdit: true,
      },
    },
  };

  componentDidMount() {
    const { autoMessage } = this.props;
    if (autoMessage) {
      this.pushAutoMessagePayloadToState(autoMessage);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.autoMessage && this.props.autoMessage) {
      this.pushAutoMessagePayloadToState(this.props.autoMessage);
    }
  }

  pushAutoMessagePayloadToState = (autoMessage: AutoMessage) => {
    this.setState({ autoMessageFields: autoMessage });
  };

  createAutoMessage = (messageName: ?string, orgUuid: ?string) => {
    if (!orgUuid || !messageName) {
      console.warn({ orgUuid, messageName });
      console.error('AdminNewAutoMessage - tried to submit with missing required fields');
      return;
    }

    this.props
      .save({
        orgUuid,
        messageName,
      })
      .then(
        res => {
          const { autoMessage, error } = res.data.saveAutoMessage;
          if (error) {
            console.warn(error);
            console.error('AdminNewAutoMessage - saveAutoMessage failure');
            ToastActions.showAPIError(error);
            return;
          }
          console.log(autoMessage);
          this.pushAutoMessagePayloadToState(autoMessage);
        },
        err => {
          console.warn(err);
          console.error('AdminNewAutoMessage - saveAutoMessage failure');
          ToastActions.showAPIError(err);
        }
      );
  };

  saveAutoMessage = (fields: *, onSuccess: Function) => {
    const { autoMessageFields } = this.state;

    if (!autoMessageFields.uuid) {
      console.warn({ autoMessageFields });
      console.error('AdminNewAutoMessage - tried to edit a nonexistent auto message');
      ToastActions.showDefaultErrorToast();
      return;
    }

    const fieldsToSubmit = {
      ...fields,
      uuid: autoMessageFields.uuid,
    };

    this.props.save(fieldsToSubmit).then(
      res => {
        const { autoMessage, error } = res.data.saveAutoMessage;
        if (error) {
          console.warn(error);
          console.error('AdminNewAutoMessage - saveAutoMessage failure');
          ToastActions.showAPIError(error);
          return;
        }
        console.log(autoMessage);
        this.pushAutoMessagePayloadToState(autoMessage);

        onSuccess();
      },
      err => {
        console.warn(err);
        console.error('AdminNewAutoMessage - saveAutoMessage failure');
        ToastActions.showAPIError(err);
      }
    );
  };

  render() {
    const { orgs, loading } = this.props;
    const { autoMessageFields } = this.state;
    const {
      uuid,
      messageName,
      orgUuid,
      csvName,
      sftpCredentials,
      messageContent,
      sender,
      includeParents,
      includeStudents,
      status,
    } = autoMessageFields;

    if (loading) {
      return <LoadingModal />;
    }

    if (!orgs || orgs.length === 0) {
      console.error('AdminNewAutoMessage - error loading user orgs');
      return null;
    }

    const validOrgs = orgs.filter(org => org.permissions.canSendToStudentIdList);
    const selectedOrg = validOrgs.find(org => org.uuid === orgUuid);

    const tabs = [
      {
        tabKey: 'format-csv',
        name: 'Format Export (CSV file)',
        component: AdminNewAutoMessageFormatCSV,
        prevStep: undefined,
        nextStep: 'sync-data',
        isComplete: Boolean(csvName),
      },
      {
        tabKey: 'sync-data',
        name: 'Set up file transfer',
        component: AdminNewAutoMessageSyncData,
        prevStep: 'format-csv',
        nextStep: 'compose',
        isComplete: Boolean(sftpCredentials),
      },
      {
        tabKey: 'compose',
        name: 'Compose',
        component: AdminNewAutoMessageCompose,
        prevStep: 'sync-data',
        nextStep: 'review',
        isComplete: Boolean(
          messageName && orgUuid && messageContent && sender && (includeParents || includeStudents)
        ),
      },
      {
        tabKey: 'review',
        name: 'Review',
        component: AdminNewAutoMessageReview,
        prevStep: 'compose',
        nextStep: undefined,
        isComplete: status === 'active',
      },
    ];

    return uuid && selectedOrg ? (
      <AdminNewAutoMessageForms
        orgs={validOrgs}
        onSave={this.saveAutoMessage}
        tabs={tabs}
        onBack={this.props.onBack}
        autoMessageFields={autoMessageFields}
        selectedOrg={selectedOrg}
        onFieldChange={this.props.onFieldChange}
        onResetFieldChange={this.props.onResetFieldChange}
        startingTab={this.props.startingTab}
      />
    ) : (
      <WizardLayout
        headerIcon="lightning-bolt"
        headerIconColor="shamrock"
        title="New auto message"
        onBack={this.props.onBack}
        nav={
          <Button href="#" color="polar-bear" level="secondary" size="xsmall">
            Help
          </Button>
        }>
        <AdminNewAutoMessageCreate
          orgs={validOrgs}
          messageName={messageName}
          orgUuid={orgUuid}
          onCreate={this.createAutoMessage}
        />
      </WizardLayout>
    );
  }
}

export default AdminNewAutoMessage;
